import React, { useMemo } from "react";
import { Table } from "antd";
import { startOfMonth, endOfMonth } from "date-fns";

interface SalaryRecord {
  _id: string;
  date: string;
  salaryTypes: {
    percentageRates: number;
  };
}

interface RevenueType {
  _id: string;
  name: string;
  sectorId: string;
}

interface RevenueByType {
  _id: string;
  revenueTypeId: RevenueType;
  totalAmount: number;
}

interface DailyRevenueItem {
  _id: string;
  date: string;
  totalAmount: number;
  revenueByType?: RevenueByType[];
}

interface WorkSector {
  _id: string;
  name: string;
}

interface PositionInSector {
  _id: string;
  name: string;
  workSector: WorkSector;
}

interface Props {
  salaryHistory: SalaryRecord[];
  selectedMonth: string;
  dailyRevenue: DailyRevenueItem[];
  positionInSector: PositionInSector[];
  isDetailyWageTableVisible: boolean;
  onTotalChange?: (totalAmount: number) => void;
  allowedRevenueNames?: string[];
}

const PercentageSalaryTable: React.FC<Props> = ({
  salaryHistory = [],
  selectedMonth,
  dailyRevenue = [],
  positionInSector = [],
  isDetailyWageTableVisible,
  onTotalChange,
  allowedRevenueNames = [],
}) => {
  const { rows, allRevenueTypes } = useMemo(() => {
    if (!selectedMonth) return { rows: [], allRevenueTypes: [] };

    const monthStart = startOfMonth(new Date(selectedMonth));
    const monthEnd = endOfMonth(new Date(selectedMonth));

    const userSectors = positionInSector
      .filter((p) => p.workSector?._id)
      .map((p) => ({
        id: p.workSector._id,
        name: p.workSector.name,
      }));

    const userSectorIds = new Set(userSectors.map((s) => s.id));

    const sortedSalaries = [...salaryHistory]
      .filter((s) => s && s.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const normalizeRate = (p: number) =>
      p == null || Number.isNaN(p) ? 0 : p >= 1 ? p / 100 : p;

    const getActiveRateForDate = (date: Date): number => {
      const valid = sortedSalaries.filter(
        (s) => new Date(s.date).getTime() <= date.getTime()
      );
      if (!valid.length) return 0;
      const last = valid[valid.length - 1];
      return normalizeRate(last.salaryTypes?.percentageRates || 0);
    };

    const filteredRevenue = dailyRevenue.filter((r) => {
      const d = new Date(r.date);
      return d >= monthStart && d <= monthEnd;
    });

    // 🔹 Unikatni tipovi prihoda
    const allRevenueTypes = Array.from(
      new Set(
        filteredRevenue.flatMap((r) =>
          (r.revenueByType || []).map((rb) => rb.revenueTypeId?.name)
        )
      )
    ).filter(Boolean) as string[];

    const rows: any[] = [];

    filteredRevenue.forEach((rev) => {
      const revDate = new Date(rev.date);
      const activeRate = getActiveRateForDate(revDate);

      const allowedRevenue = (rev.revenueByType || []).filter((rbt) => {
        const inSector = userSectorIds.has(rbt.revenueTypeId?.sectorId);
        const allowedByName =
          allowedRevenueNames.length === 0 ||
          allowedRevenueNames.includes(rbt.revenueTypeId?.name);
        return inSector && allowedByName;
      });

      const revenueMap: Record<string, number> = {};
      allRevenueTypes.forEach((t) => (revenueMap[t] = 0));
      allowedRevenue.forEach((r) => {
        if (r.revenueTypeId?.name)
          revenueMap[r.revenueTypeId.name] = r.totalAmount;
      });

      const sectorName =
        userSectors.find((s) =>
          allowedRevenue.some((rbt) => rbt.revenueTypeId?.sectorId === s.id)
        )?.name ?? "";

      const totalSectorRevenue = Object.values(revenueMap).reduce(
        (a, b) => a + b,
        0
      );

      rows.push({
        key: rev._id,
        date: revDate.toLocaleDateString("sr-RS"),
        sector: sectorName,
        rate: `${(activeRate * 100).toFixed(2)} %`,
        amount: totalSectorRevenue * activeRate,
        totalAmount: totalSectorRevenue,
        ...revenueMap,
      });
    });

    return { rows, allRevenueTypes };
  }, [
    salaryHistory,
    selectedMonth,
    dailyRevenue,
    positionInSector,
    allowedRevenueNames,
  ]);

  const totalEarned = rows.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalRevenue = rows.reduce((sum, r) => sum + (r.totalAmount || 0), 0);

  if (onTotalChange) onTotalChange(totalEarned);
  if (!isDetailyWageTableVisible) return null;

  const columns = [
    { title: "Datum", dataIndex: "date" },
    { title: "Sektor", dataIndex: "sector" },
    ...allRevenueTypes.map((name) => ({
      title: name,
      dataIndex: name,
      render: (v: number) =>
        typeof v === "number"
          ? v.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : "0,00",
    })),
    {
      title: "Ukupan prihod",
      dataIndex: "totalAmount",
      render: (v: number) =>
        v?.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    { title: "Procenat", dataIndex: "rate" },
    {
      title: "Zarada (RSD)",
      dataIndex: "amount",
      render: (v: number) =>
        v?.toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={rows}
      pagination={false}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <strong>Ukupno</strong>
          </Table.Summary.Cell>

          {allRevenueTypes.map((name, i) => (
            <Table.Summary.Cell key={name} index={i + 2}>
              <strong>
                {rows
                  .reduce((sum, r) => sum + (r[name] || 0), 0)
                  .toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>
            </Table.Summary.Cell>
          ))}

          <Table.Summary.Cell index={allRevenueTypes.length + 2}>
            <strong>
              {totalRevenue.toLocaleString("sr-RS", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          </Table.Summary.Cell>

          <Table.Summary.Cell index={allRevenueTypes.length + 3}>
            {/* prazna ćelija za "Procenat" */}
          </Table.Summary.Cell>

          <Table.Summary.Cell index={allRevenueTypes.length + 4}>
            <strong>
              {totalEarned.toLocaleString("sr-RS", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default PercentageSalaryTable;
